'use client';
import React, { FC, useMemo, useState } from 'react';
import { TypeCoursePlayerFields, TypeImage } from '@/core/generated-types';
import {
  Button,
  CourseChapter,
  CourseSupportingContent,
  HeaderText,
  RichText,
  Tabs,
} from '@/components';
import {
  ExpandedTabDisplayTypes,
  NavigationDisplayTypes,
} from '@/components/common/Tabs/Tabs';
import { Image } from '@/components/ui/Image';
import { HeaderSize } from '@/components/ui/HeaderText';
import { Modal } from '@/components/ui/Modal';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import useCourseNavigation from '@/components/courses/CoursePlayer/useCourseNavigation';
import { TypeUnsignedMuxVideoAsset } from '@/components/courses/CourseVideo/utils';
import { interpolate } from '@/utils/translations';
import CourseVideoPreview from '@/components/courses/CourseVideo/CourseVideoPreview';
import { Document } from '@contentful/rich-text-types';

export const CoursePlayerPreview: FC<TypeCoursePlayerFields> = ({
  courseProduct,
  coursePreviewVideoDetails,
  coursePreviewVideo,
  coursePreviewVideoTitle,
  additionalSupport,
}) => {
  const [isCoursePreviewOpen, setIsCoursePreviewOpen] = useState(false);
  const courseNavigation = useCourseNavigation(courseProduct);
  const tabs = useMemo(() => {
    return courseProduct.fields.courseParts.map((coursePart, i) => ({
      title: interpolate(coursePart.fields.titleWithSectionPrefix, {
        currentSection: i + 1,
        sectionCount: courseProduct.fields.courseParts.length,
      }),
      onSelectTab: () => courseNavigation.navigateToPosition(coursePart.sys.id),
      jsxContent: (
        <>
          {coursePart.fields.chapters.map((chapter, i) => (
            <CourseChapter
              key={chapter.sys.id}
              courseChapter={chapter}
              isActiveChapter={
                chapter.sys.id === courseNavigation.navigationPosition.chapterId
              }
              isFirstChapter={i === 0}
              navigateToChapter={() =>
                courseNavigation.navigateToChapter(chapter.sys.id)
              }
              closeChapter={() => courseNavigation.navigateToChapter('')}
              enablePreviewMode
            />
          ))}
        </>
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseProduct.fields.courseParts, courseNavigation]);

  const { textHeading, textBody, image, cta } =
    coursePreviewVideoDetails?.fields || {};

  return (
    <div data-testid="course-player-preview-container">
      <Modal
        isOpen={isCoursePreviewOpen}
        handleClose={() => setIsCoursePreviewOpen(false)}
      >
        {coursePreviewVideo && (
          <CourseVideoPreview
            muxVideo={coursePreviewVideo.fields as TypeUnsignedMuxVideoAsset}
            title={coursePreviewVideoTitle}
            isSigned={false}
          />
        )}
      </Modal>
      <div className="lg:container lg:mx-auto lg:grid lg:max-w-[1200px] lg:grid-cols-12 lg:gap-6">
        <div className="mx-4 text-left lg:col-span-4 lg:mx-0 lg:max-w-[450px]">
          <Image
            imageData={image as TypeImage}
            className="h-auto w-full"
            alt={image?.fields?.altText}
          />
          <HeaderText
            text={textHeading}
            size={HeaderSize.H3}
            align="left"
            classes="mt-3 mb-1"
          />
          {textBody && <RichText content={textBody as Document} />}
          <p className="text-left">
            {coursePreviewVideo && (
              <Button
                style={BUTTON_STYLES.link}
                size={BUTTON_SIZES.none}
                onClick={() => setIsCoursePreviewOpen(true)}
                classes="ml-2"
                ctaText={cta?.fields?.linkText}
                testId="open-course-preview-video-modal"
              />
            )}
          </p>
        </div>
        <div
          className="mb-8 mt-6 lg:col-span-8 lg:mx-0 lg:mb-0 lg:mt-0 lg:flex lg:flex-col"
          data-testid="course-player-navigation-container"
        >
          <div className="lg:relative lg:h-full">
            <div className="lg:absolute lg:left-0 lg:top-0 lg:flex lg:h-full lg:w-full lg:flex-col lg:overflow-y-scroll lg:overscroll-contain">
              <Tabs
                tabs={tabs}
                expandedTabDisplayType={ExpandedTabDisplayTypes.JSX}
                navigationDisplayType={NavigationDisplayTypes.Single}
              />
            </div>
          </div>
        </div>
      </div>
      {additionalSupport && (
        <div
          className="lg:mx-auto lg:mt-14 lg:max-w-[1200px] lg:border-t lg:border-charcoal-opacity-20"
          data-testid="course-player-additional-support-container"
        >
          <div className="mt-14 grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
            {additionalSupport.map((additionalSupportItem) => (
              <CourseSupportingContent
                key={additionalSupportItem.sys.id}
                {...additionalSupportItem.fields}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
